import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";

import ProjectGroup from "./ProjectGroup";
import NewProjectRow from "./NewProjectRow";
import { COLORS, RADIUS } from "./ticketTheme";
import { useProjects } from "../../store/useProjects";

export default function ProjectsBoard({
  visibility,
  orgUsers,
  creatingProject,
  onCreatingProjectChange,
}) {
  const {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    addTask,
    updateTask,
    moveTask,
    assignTask,
    deleteTask,
  } = useProjects({ visibility });

  // Same flattening NewTicketDialog does for its assignee picker — the raw
  // /users/list_users shape down to {id, name}.
  const assignableUsers = useMemo(
    () =>
      (orgUsers || []).map((u) => ({
        id: u.id,
        name:
          `${u.name || ""} ${u.last_name || ""}`.trim() ||
          u.username ||
          u.email ||
          "Unnamed",
      })),
    [orgUsers]
  );

  const handleCreateProject = async (payload) => {
    await createProject({ ...payload, visibility });
    onCreatingProjectChange(false);
  };

  if (loading) {
    return (
      <Typography sx={{ color: COLORS.textSecondary, mt: 4 }}>
        Loading projects…
      </Typography>
    );
  }

  return (
    <Box>
      {creatingProject && (
        <Box sx={{ mb: 3 }}>
          <NewProjectRow
            onCreate={handleCreateProject}
            onCancel={() => onCreatingProjectChange(false)}
          />
        </Box>
      )}

      {projects.length === 0 && !creatingProject ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: COLORS.textPrimary }}
          >
            No projects yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: COLORS.textSecondary, mb: 2 }}
          >
            Create your first project to start tracking tasks.
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => onCreatingProjectChange(true)}
            sx={{
              bgcolor: COLORS.accent,
              color: "#fff",
              border: "none",
              borderRadius: `${RADIUS.field}px`,
              px: 2.5,
              py: 1,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            + New project
          </Box>
        </Box>
      ) : (
        projects.map((project) => (
          <ProjectGroup
            key={project.id}
            project={project}
            orgUsers={assignableUsers}
            onAddTask={() => addTask(project.id, { title: "New task" })}
            onUpdateTask={(taskId, changes) =>
              updateTask(project.id, taskId, changes)
            }
            onMoveTask={(taskId, newStatus) =>
              moveTask(project.id, taskId, newStatus)
            }
            onAssignTask={(taskId, uid) => assignTask(project.id, taskId, uid)}
            onDeleteTask={(taskId) => deleteTask(project.id, taskId)}
            onRenameProject={(name) => updateProject(project.id, { name })}
            onDeleteProject={() => deleteProject(project.id)}
          />
        ))
      )}

      {projects.length > 0 && !creatingProject && (
        <Box
          component="button"
          type="button"
          onClick={() => onCreatingProjectChange(true)}
          sx={{
            border: "1px dashed",
            borderColor: COLORS.borderStrong,
            bgcolor: "transparent",
            color: COLORS.textSecondary,
            borderRadius: `${RADIUS.pill}px`,
            px: 2.5,
            py: 1,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          + Add group
        </Box>
      )}
    </Box>
  );
}
